import React, { useRef, useState } from "react";
import { LoadingComponent } from "rylak-common/src/components/loadingComponent/LoadingComponent.jsx";
import { ServiceErrorAlert } from "~/components/alerts/Error";
import styles from "./index.module.scss";
import * as utils from "rylak-common/src/utils";
import { getEmailValidators, getOrderIdValidators, isLoginInputValid } from "./index.core";
import { ExternalLink } from "~/components/links/ExternalLink";
import classnames from "classnames";
import Input from "~/components/inputs/Input";
import getServerSideProps from "../../app/getServerSideProps";
import { Button } from "~/components/buttons/Buttons";
import { getBrandPrivacyPolicyUrl, getBrandReturnsPolicyUrl, getBrandTermsAndConditionsUrl, getOrderIdLabelId, showAllOrdersBrand } from "~/cores/brandDataCore";
import { Checkbox } from "~/components/inputs/CheckBox";
import { QUERY_AND_SESSION_KEYS } from "~/constants";
import { TurnrInfoAlert } from "~/components/alerts/TurnrAlert";
import { serviceIsLoading, serviceIsSuccessfullyFetched } from "rylak-common";
import { useBrowserState } from "rylak-common/src/hooks/useBrowserStateOld.jsx";

const scrollToRef = (ref) =>
    ref.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
    });

function LoginFormContainer(props) {
    const { appProps, brandData, loginServiceData, getLocaleText, appOverrides, email, orderId, setEmail, setOrderId, tryLogin } = props;

    const [validated, setValidated] = useState(false);

    const [termsAndConditionsValue, setTermsAndConditionsValue] = useBrowserState({
        key: QUERY_AND_SESSION_KEYS.ACCEPT_POLICY,
        defaultValue: "",
        shouldUpdateQuery: false,
    });

    const inputsRef = useRef(null);

    const emailValidators = getEmailValidators(props);

    const orderIdValidators = getOrderIdValidators(props);

    const validInput = isLoginInputValid(appProps, {
        termsAndConditionsValue,
        email,
        orderId,
    });

    const handleSubmit = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (validInput) {
            tryLogin();
            setValidated(false);
        } else {
            setValidated(true);
        }
    };

    const handleEmailChange = (value) => {
        setEmail(value);
    };

    const handleOrderIdChange = (value) => {
        setOrderId(value.toUpperCase());
    };

    const executeScroll = () => {
        if (utils.isAndroid(navigator)) {
            // A bit hacky but seem to do the trick for android :)
            setTimeout(() => scrollToRef(inputsRef), 1);
            setTimeout(() => scrollToRef(inputsRef), 25);
            setTimeout(() => scrollToRef(inputsRef), 50);
            setTimeout(() => scrollToRef(inputsRef), 75);
            setTimeout(() => scrollToRef(inputsRef), 100);
            setTimeout(() => scrollToRef(inputsRef), 125);
            setTimeout(() => scrollToRef(inputsRef), 150);
            scrollToRef(inputsRef);
        }
    };

    const loading = serviceIsLoading(loginServiceData) || serviceIsSuccessfullyFetched(loginServiceData);

    const termsUrl = getBrandTermsAndConditionsUrl(appProps);
    const privacyPolicyUrl = getBrandPrivacyPolicyUrl(appProps);
    const returnPolicyUrl = getBrandReturnsPolicyUrl(appProps);

    const showTermsOrPrivacyPolicy = !!(termsUrl || privacyPolicyUrl);
    const showAnyPolicy = !!(showTermsOrPrivacyPolicy || returnPolicyUrl);

    const showTermsAndConditionsError = validated && !termsAndConditionsValue;

    return (
        <section className={styles["section"]}>
            <form style={{ height: "100%" }} onSubmit={handleSubmit} noValidate>
                <div className={styles["inputs-wrapper"]}>
                    <div className={styles["inputs-size-limiter"]} style={{ marginBottom: "25px" }}>
                        <div style={{ marginBottom: "20px" }}>
                            <Input
                                appProps={appProps}
                                name="email"
                                value={email}
                                onClick={executeScroll}
                                onChange={handleEmailChange}
                                type="email"
                                placeholder={props.getLocaleText("login.email_placeholder")}
                                disabled={loading}
                                required={true}
                                validated={validated}
                                validators={emailValidators}
                            />
                        </div>
                        {showAllOrdersBrand(props) ? (
                            <Input
                                appProps={appProps}
                                ref={inputsRef}
                                name="zipCode"
                                value={orderId}
                                onClick={executeScroll}
                                onChange={handleOrderIdChange}
                                type={"text"}
                                placeholder={props.getLocaleText("login.ZIP_CODE")}
                                disabled={appOverrides?.orderIdDisabled || loading}
                                required={true}
                                validated={validated}
                                validators={orderIdValidators}
                            />
                        ) : (
                            <Input
                                appProps={appProps}
                                ref={inputsRef}
                                name="orderId"
                                value={orderId}
                                onClick={executeScroll}
                                onChange={handleOrderIdChange}
                                type={"text"}
                                placeholder={props.getLocaleText(`login.${getOrderIdLabelId(props)}`)}
                                disabled={appOverrides?.orderIdDisabled || loading}
                                required={true}
                                validated={validated}
                                validators={orderIdValidators}
                                prefix={brandData?.orderIdPrefix}
                            />
                        )}
                    </div>
                    {showAnyPolicy && (
                        <div style={{ marginBottom: "25px" }}>
                            <div
                                style={{
                                    display: "flex",
                                    padding: "15px",
                                    margin: "-15px",
                                    flexDirection: "row",
                                    flexWrap: "no-wrap",
                                    alignItems: "center",
                                    cursor: showTermsOrPrivacyPolicy && "pointer",
                                }}
                                onClick={() => {
                                    !loading && setTermsAndConditionsValue(!termsAndConditionsValue);
                                }}
                            >
                                {showTermsOrPrivacyPolicy && (
                                    <Checkbox
                                        style={{ marginRight: "10px" }}
                                        disabled={loading}
                                        dataTestId={"terms-and-conditions-checkbox"}
                                        error={showTermsAndConditionsError}
                                        checked={termsAndConditionsValue}
                                        onChange={() => setTermsAndConditionsValue(!termsAndConditionsValue)}
                                    />
                                )}
                                <p className={classnames(styles["terms-and-conditions-intro"], "potentially-black")}>
                                    {showTermsOrPrivacyPolicy && <>{getLocaleText("terms-and-conditions-intro")} </>}
                                    {termsUrl && (
                                        <ExternalLink href={termsUrl} onClick={(e) => e.stopPropagation()} className={styles["terms-and-conditions"]} text={getLocaleText("terms-and-conditions")} />
                                    )}
                                    {privacyPolicyUrl && termsUrl && <> {getLocaleText("and")} </>}
                                    {privacyPolicyUrl && (
                                        <ExternalLink href={privacyPolicyUrl} onClick={(e) => e.stopPropagation()} className={styles["terms-and-conditions"]} text={getLocaleText("privacy-policy")} />
                                    )}
                                    {showTermsOrPrivacyPolicy && returnPolicyUrl && ". See "}
                                    {returnPolicyUrl ? (
                                        <ExternalLink href={returnPolicyUrl} onClick={(e) => e.stopPropagation()} className={styles["terms-and-conditions"]} text={getLocaleText("return-policy")} />
                                    ) : null}
                                </p>
                            </div>
                            {showTermsAndConditionsError && (
                                <TurnrInfoAlert style={{ marginTop: "15px" }} fullWidth={true}>
                                    {getLocaleText("terms-and-conditions-error")}
                                </TurnrInfoAlert>
                            )}
                        </div>
                    )}

                    <div className={classnames(styles["login-button"], !showAnyPolicy && styles["login-button-no-terms"])}>
                        <Button
                            appProps={appProps}
                            style={{ height: "60px", marginTop: "20px" }}
                            dataTestId={"continue-btn"}
                            fullWidth={true}
                            variant={"primary"}
                            getLocaleText={getLocaleText}
                            loading={loading}
                            type="submit"
                            text={getLocaleText("login.submit_button")}
                        />
                    </div>
                </div>
            </form>
        </section>
    );
}

function LoginPage(props) {
    const { getLocaleText, initServiceData } = props;

    return (
        <div>
            {/*<button onClick={() => {*/}
            {/*    navigate({*/}
            {/*        pathname: "/[brand]/testing",*/}
            {/*    }, undefined, {shallow: true});*/}
            {/*}}>Test</button>*/}
            {/*Google Tag Manager (noscript)*/}
            <noscript>
                <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PMRN3VM" height="0" width="0" style={{ display: "none", visibility: "hidden" }} />
            </noscript>
            {/* End Google Tag Manager (noscript) */}
            {!initServiceData?.fetched && (
                <div style={{ marginTop: "10px" }}>
                    <LoadingComponent getLocaleText={getLocaleText} />
                </div>
            )}
            <ServiceErrorAlert serviceData={initServiceData} getLocaleText={getLocaleText} localeKey={"init"} style={{ marginTop: "20px" }} />
            {initServiceData?.data && (
                <>
                    <LoginFormContainer {...props} />
                </>
            )}
        </div>
    );
}

export { getServerSideProps, LoginFormContainer };
export default LoginPage;
